<template>
  <div class="portal-page">
    <!-- Top Bar Navigation (PORTAL.MFU Style with System Blue Theme) -->
    <header class="portal-header">
      <div class="portal-header-left">
        <!-- Smartfix System Title (Matching inside navbar without logo/ribbon) -->
        <div class="desktop-system-title">
          <span class="system-main-title">{{ $t('login.title') }}</span>
          <span class="system-sub-title">{{ $t('login.subtitle') }}</span>
        </div>
      </div>

      <!-- Language Switcher: EN | TH in System Blue Style -->
      <div class="portal-header-right">
        <div class="portal-lang-switcher" role="group" aria-label="Language Switcher">
          <button
            type="button"
            class="lang-btn"
            :class="{ active: currentLang === 'en' }"
            @click="setLang('en')"
          >
            EN
          </button>
          <span class="lang-divider">|</span>
          <button
            type="button"
            class="lang-btn"
            :class="{ active: currentLang === 'th' }"
            @click="setLang('th')"
          >
            TH
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content Area -->
    <main class="portal-main">
      <div class="portal-card">
        <!-- Top Blue Accent Line -->
        <div class="portal-card-accent-bar"></div>

        <div class="portal-card-body">
          <div class="portal-brand-header text-center">
            <h1 class="portal-card-title">{{ $t('login.signIn') || 'Sign In' }}</h1>
            <p class="portal-card-subtitle">
              {{ $t('login.title') }} · {{ $t('login.subtitle') }}
            </p>
          </div>

          <!-- Primary Login: Google Auth / MFU Mail -->
          <div class="portal-auth-section">
            <button
              type="button"
              class="btn-google-portal"
              @click="triggerGoogleLogin"
              :title="$t('login.signIn')"
            >
              <div class="google-icon-wrapper">
                <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"/>
                  <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 10.04 0 12s.45 3.82 1.25 5.42l4.03-3.15z"/>
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
                </svg>
              </div>
              <span class="btn-google-text">
                {{ currentLang === 'en' ? 'Sign in with MFU Mail (Google)' : 'เข้าสู่ระบบด้วย MFU Mail (Google)' }}
              </span>
            </button>

            <!-- Notice & Hint in Blue Pill Container -->
            <div class="portal-hint-box">
              <div class="hint-line font-weight-bold">
                <CIcon name="cil-info" class="mr-1 text-primary" size="sm" />
                {{ $t('login.mfuHint') }}
              </div>
              <div class="hint-sub">
                {{ $t('login.externalNotice') }}
              </div>
            </div>
          </div>

          <!-- Development Mock Login Drawer (for Local & Testing) -->
          <div v-if="isMockEnabled" class="portal-dev-drawer">
            <button
              type="button"
              class="portal-dev-toggle"
              @click="devExpanded = !devExpanded"
              :aria-expanded="devExpanded"
            >
              <span class="dev-toggle-title">
                <span class="dev-badge">DEV</span>
                {{ $t('login.mockTitle') || 'DEVELOPMENT LOGIN' }}
              </span>
              <span class="portal-dev-chevron">{{ devExpanded ? '▲' : '▼' }}</span>
            </button>

            <div v-show="devExpanded" class="portal-dev-body">
              <!-- Role Selection -->
              <div class="dev-form-row">
                <label class="dev-form-label">{{ $t('login.mockRoleLabel') }}</label>
                <div class="dev-select-wrapper">
                  <span class="dev-role-icon">{{ selectedMockRoleIcon }}</span>
                  <select v-model="selectedMockRole" class="dev-select" @change="onMockRoleChange">
                    <option value="user">{{ $t('login.mockRoleUser') }}</option>
                    <option value="technician">{{ $t('login.mockRoleTech') }}</option>
                    <option value="student_affairs">{{ currentLang === "en" ? "Student Affairs" : "ส่วนพัฒนานักศึกษา" }}</option>
                    <option value="supervisor">{{ $t('login.mockRoleSup') }}</option>
                    <option value="admin">{{ $t('login.mockRoleAdmin') }}</option>
                  </select>
                </div>
              </div>

              <!-- Persona Selection -->
              <div class="dev-form-row">
                <label class="dev-form-label">{{ $t('login.mockPersonaLabel') }}</label>
                <select v-model="selectedPersonaId" class="dev-select dev-select-persona" @change="onPersonaChange">
                  <option value="" disabled>{{ $t('login.mockPersonaPlaceholder') }}</option>
                  <option v-for="persona in roleMockPersonas" :key="persona.id" :value="persona.id">
                    {{ personaOptionLabel(persona) }}
                  </option>
                </select>
              </div>

              <!-- 1-Click Mock Login Button in System Blue -->
              <button type="button" class="btn-dev-submit" @click="submitMockLogin">
                <span class="mr-1">{{ selectedMockRoleIcon }}</span>
                <span>{{ $t('login.mockLoginBtn', { role: selectedMockRoleLabel }) }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Info -->
      <footer class="portal-footer">
        <button type="button" class="btn-user-manual" @click="onAuthenGoogle">
          <CIcon name="cil-book" size="sm" class="mr-1 text-primary" /> {{ $t('login.userManual') || 'คู่มือการใช้งาน' }}
        </button>
        <div class="portal-copyright">
          © 2026 Mae Fah Luang University. All rights reserved.
        </div>
      </footer>
    </main>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import api from '@/service/api';
import {
  MOCK_PERSONAS,
  filterMockPersonasByRole,
  formatMockPersonaOption,
  mapPersonaToDevAuthPayload,
  isMockAuthEnabled
} from '@/utils/mockPersona';

export default {
  name: 'SignIn',
  data: function () {
    return {
      authClient: null,
      devExpanded: false,

      selectedMockRole: 'user',
      selectedPersonaId: 'user-01',

      mockJobType: '',
      mockSectionId: '',

      debugMsg: '',
      jobTypes: [],
      mockSections: [],
      mockPersonas: MOCK_PERSONAS,

      isMockEnabled: isMockAuthEnabled(process.env.VUE_APP_MOCK_AUTH_ENABLED)
    };
  },
  computed: {
    currentLang() {
      return this.$store?.state?.locale?.lang || 'th';
    },

    selectedPersona() {
      return this.mockPersonas.find(p => p.id === this.selectedPersonaId) || null;
    },

    roleMockPersonas() {
      return filterMockPersonasByRole(this.mockPersonas, this.selectedMockRole);
    },

    selectedMockRoleLabel() {
      const labels = {
        user: this.$t('login.mockRoleUser'),
        technician: this.$t('login.mockRoleTech'),
        supervisor: this.$t('login.mockRoleSup'),
        student_affairs: this.currentLang === 'en' ? 'Student Affairs' : 'ส่วนพัฒนานักศึกษา',
        admin: this.$t('login.mockRoleAdmin')
      };

      return labels[this.selectedMockRole] || this.$t('login.mockRoleUser');
    },

    selectedMockRoleIcon() {
      const icons = {
        user: '👤',
        technician: '🔧',
        supervisor: '👔',
        student_affairs: '🎓',
        admin: '👑'
      };

      return icons[this.selectedMockRole] || '👤';
    }
  },
  mounted() {
    this.checkAndInitGoogleAuth();
    this.fetchJobTypes();
    if (this.$route.query.code) {
      this.sendCodeToBackend(this.$route.query.code);
    }
  },
  methods: {
    setLang(lang) {
      this.$store.commit('locale/SET_LANG', lang);
    },

    onAuthenGoogle() {
      Swal.fire({
        icon: 'info',
        title: this.$t('login.userManual') || 'คู่มือการใช้งาน',
        text: this.currentLang === 'en'
          ? 'The SmartFix user manual is accessible through the top navigation bar once logged in.'
          : 'สามารถศึกษาคู่มือการใช้งานระบบแจ้งซ่อมออนไลน์ (SmartFix) ได้ที่แถบเมนูด้านบนหลังจากเข้าสู่ระบบ',
        confirmButtonText: this.$t('login.ok') || 'ตกลง',
        confirmButtonColor: '#1e40af'
      });
    },

    async fetchJobTypes() {
      this.debugMsg = 'Starting fetch...';
      try {
        const response = await api.category('get');
        if (response.data && response.data.success) {
          const list = [];
          const sections = [];
          response.data.data.forEach(dept => {
            if (dept.subCategories && Array.isArray(dept.subCategories)) {
              const activeJobs = dept.subCategories.filter(sub => sub.isActive !== false);
              activeJobs.forEach(sub => list.push(sub.name));
              if (dept.isActive !== false && activeJobs.length > 0) {
                sections.push({
                  _id: dept._id,
                  displayName: dept.departmentName
                });
              }
            }
          });
          this.jobTypes = list;
          this.mockSections = sections;
          this.debugMsg = `Loaded ${list.length} jobs: ` + list.join(', ');
        } else {
          this.debugMsg = 'API response missing success or data';
        }
      } catch (error) {
        console.error('Failed to fetch job types:', error);
        this.debugMsg = error.message || String(error);
      }
    },

    checkAndInitGoogleAuth() {
      if (typeof window.google !== 'undefined' && window.google.accounts) {
        this.initGoogleAuth();
      } else {
        const interval = setInterval(() => {
          if (typeof window.google !== 'undefined' && window.google.accounts) {
            clearInterval(interval);
            this.initGoogleAuth();
          }
        }, 300);
      }
    },

    initGoogleAuth() {
      if (typeof window.google !== 'undefined') {
        this.authClient = window.google.accounts.oauth2.initCodeClient({
          client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
          scope: 'openid profile email',
          ux_mode: 'popup',
          callback: (response) => {
            if (response.code) {
              this.sendCodeToBackend(response.code);
            }
          },
        });
      }
    },

    triggerGoogleLogin() {
      if (!this.authClient && typeof window.google !== 'undefined') {
        this.initGoogleAuth();
      }

      if (this.authClient) {
        this.authClient.requestCode();
      } else {
        Swal.fire({
          icon: 'warning',
          title: this.$t('login.swalGoogleLoadingTitle'),
          text: this.$t('login.swalGoogleLoadingText'),
          confirmButtonText: this.$t('login.ok')
        });
      }
    },

    personaOptionLabel(persona) {
      return formatMockPersonaOption(persona);
    },

    onMockRoleChange() {
      const available = this.roleMockPersonas;
      this.selectedPersonaId = available.length > 0 ? available[0].id : '';
    },

    onPersonaChange() {
      if (this.selectedPersona && this.selectedPersona.role !== this.selectedMockRole) {
        this.selectedMockRole = this.selectedPersona.role;
      }
    },

    submitMockLogin() {
      const persona = this.selectedPersona;
      if (!persona) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('mgmt.warn'),
          text: this.$t('login.mockPersonaRequired'),
          confirmButtonText: this.$t('login.ok')
        });
        return;
      }

      const payload = mapPersonaToDevAuthPayload(persona, this.mockSections);
      if (!payload || !payload.role) {
        return;
      }

      this.mockLogin(payload.role, payload.jobType, payload.sectionId, persona.id);
    },

    async mockLogin(role, jobType = null, sectionId = null, personaId = null) {
      if (role === 'technician' && !jobType) {
        Swal.fire(this.$t('mgmt.warn'), this.$t('login.mockJobRequired'), 'warning');
        return;
      }
      if (role === 'supervisor' && !sectionId) {
        Swal.fire(this.$t('mgmt.warn'), this.$t('login.mockSectionRequired'), 'warning');
        return;
      }
      try {
        Swal.fire({
          title: this.$t('login.verifyingTitle'),
          text: this.$t('login.verifyingText'),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await this.$store.dispatch('auth/mockSignIn', { role, jobType, sectionId, personaId });

        Swal.fire({
          icon: 'success',
          title: this.$t('login.successTitle'),
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          window.location.reload();
        });
      } catch (err) {
        console.error('Backend error:', err);
        Swal.fire({
          icon: 'error',
          title: this.$t('login.errorTitle'),
          text: err.response?.data?.message || err.message || this.$t('login.failGeneric'),
          confirmButtonText: this.$t('login.ok')
        });
      }
    },

    async sendCodeToBackend(code) {
      try {
        Swal.fire({
          title: this.$t('login.verifyingTitle'),
          text: this.$t('login.verifyingText'),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await this.$store.dispatch('auth/signIn', { code: code });

        Swal.fire({
          icon: 'success',
          title: this.$t('login.successTitle'),
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          window.location.reload();
        });
      } catch (error) {
        console.error('Login error:', error);
        Swal.fire({
          icon: 'error',
          title: this.$t('login.failTitle'),
          text: this.$t('login.failGeneric'),
          confirmButtonText: this.$t('login.ok')
        });
      }
    }
  }
};
</script>

<style scoped>
/* Page Layout */
.portal-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%);
  font-family: "Sarabun", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  color: #1e293b;
}

.portal-page button,
.portal-page input,
.portal-page select,
.portal-page textarea,
.portal-page h1,
.portal-page h2,
.portal-page p,
.portal-page span,
.portal-page div {
  font-family: "Sarabun", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* Header - Exact 1:1 match with inside system navbar */
.portal-header {
  height: 76px;
  min-height: 76px;
  background: #ffffff;
  border-bottom: 1px solid rgba(37, 99, 235, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  position: relative;
  z-index: 10;
}

.portal-header-left {
  display: flex;
  align-items: center;
}

.desktop-system-title {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.16;
}

.desktop-system-title .system-main-title {
  color: #1e40af;
  font-size: clamp(20px, 1.4vw, 24px);
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.16;
  white-space: nowrap;
}

.desktop-system-title .system-sub-title {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  margin-top: 3px;
  white-space: nowrap;
}

/* Language Switcher - Exact same sizing as inside system LanguageSwitcher.vue */
.portal-lang-switcher {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  margin-right: 0.5rem;
}

.lang-btn {
  border: none;
  background: transparent;
  padding: 0.35rem 0.5rem;
  min-width: 2.25rem;
  min-height: 2.25rem;
  color: #64748b;
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1.2;
  transition: all 0.15s ease;
}

.lang-btn:hover {
  color: #1e40af;
  background: rgba(37, 99, 235, 0.08);
}

.lang-btn.active {
  color: #1e40af;
  font-weight: 700;
  background: rgba(37, 99, 235, 0.08);
}

.lang-divider {
  color: #cbd5e1;
  font-weight: 300;
}

/* Main Body & Center Card */
.portal-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px 28px;
}

.portal-card {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.15);
  border-radius: 16px;
  box-shadow: 0 10px 30px -5px rgba(30, 64, 175, 0.08), 0 4px 12px -2px rgba(30, 64, 175, 0.04);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.portal-card-accent-bar {
  height: 4px;
  background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
  width: 100%;
}

.portal-card-body {
  padding: 38px 40px 32px;
}

.portal-card-title {
  font-size: 1.95rem;
  font-weight: 700;
  color: #1e3a8a;
  text-align: center;
  margin-bottom: 6px;
  letter-spacing: -0.02em;
}

.portal-card-subtitle {
  font-size: 0.88rem;
  color: #64748b;
  margin-bottom: 28px;
}

/* Primary Google Sign In Button */
.portal-auth-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.btn-google-portal {
  width: 100%;
  min-height: 50px;
  padding: 8px 18px;
  background: #ffffff;
  border: 1.5px solid rgba(37, 99, 235, 0.22);
  border-radius: 12px;
  color: #1e40af;
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(30, 64, 175, 0.06);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-google-portal:hover {
  background: #eff6ff;
  border-color: #2563eb;
  color: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.16);
}

.btn-google-portal:active {
  transform: translateY(0);
}

.google-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-google-text {
  letter-spacing: 0.01em;
}


/* Hint Box in System Blue */
.portal-hint-box {
  background: #eff6ff;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #1e40af;
  text-align: center;
}

.hint-line {
  margin-bottom: 2px;
}

.hint-sub {
  color: #3b82f6;
  font-size: 0.72rem;
}

/* Dev Mock Drawer */
.portal-dev-drawer {
  margin-top: 24px;
  border-top: 1px dashed rgba(37, 99, 235, 0.2);
  padding-top: 16px;
}

.portal-dev-toggle {
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.portal-dev-toggle:hover {
  color: #1e40af;
  background: #eff6ff;
}

.dev-toggle-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dev-badge {
  background: #1e40af;
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 4px;
  letter-spacing: 0.05em;
}

.portal-dev-body {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dev-form-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dev-form-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 0;
}

.dev-select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.dev-role-icon {
  position: absolute;
  left: 10px;
  pointer-events: none;
  font-size: 0.85rem;
}

.dev-select {
  width: 100%;
  height: 36px;
  padding: 0 10px 0 34px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #1e293b;
  background-color: #f8fafc;
  outline: none;
}

.dev-select-persona {
  padding-left: 10px;
}

.dev-select:focus {
  border-color: #2563eb;
  background-color: #ffffff;
}

.btn-dev-submit {
  width: 100%;
  height: 38px;
  margin-top: 4px;
  background: #1e40af;
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.84rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}

.btn-dev-submit:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

/* Footer */
.portal-footer {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.btn-user-manual {
  border: none;
  background: transparent;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e40af;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-user-manual:hover {
  background: #eff6ff;
  text-decoration: underline;
}

.portal-copyright {
  font-size: 0.72rem;
  color: #94a3b8;
}

/* Mobile Responsiveness */
@media (max-width: 576px) {
  .portal-header {
    padding: 0 16px;
    height: 60px;
    min-height: 60px;
  }
  .desktop-system-title .system-main-title {
    font-size: 16px !important;
  }
  .desktop-system-title .system-sub-title {
    font-size: 11px !important;
  }
  .portal-card-body {
    padding: 28px 20px 24px;
  }
  .portal-card-title {
    font-size: 1.65rem;
  }
}
</style>
